import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText, Building2, CheckCircle2, Clock, Eye, XCircle, Check } from "lucide-react";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { fmtSalary } from "@/components/JobCard";
import { Button } from "@/components/ui/button";

const STATUS = {
  submitted: { key: "submitted", icon: CheckCircle2, color: "text-cyan-300 bg-cyan-500/10 border-cyan-500/30" },
  reviewing: { key: "reviewing", icon: Eye, color: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  accepted: { key: "accepted", icon: CheckCircle2, color: "text-teal-300 bg-teal-500/10 border-teal-500/30" },
  rejected: { key: "rejected", icon: XCircle, color: "text-red-300 bg-red-500/10 border-red-500/30" },
};

export default function Applications() {
  const { t } = useTranslation();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/applications").then((r) => setApps(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen wo-bg">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8 pb-28 lg:pb-8">
        <h1 className="font-display font-bold text-3xl text-slate-100 flex items-center gap-3"><FileText className="text-teal-400" /> {t("page.myApplications")}</h1>
        <p className="text-slate-400 text-sm mt-1 mb-6">{apps.length} {t("page.submittedN")}</p>

        {loading ? (
          <p className="text-slate-500">{t("radar.scanning")}</p>
        ) : apps.length === 0 ? (
          <div className="liquid-glass rounded-2xl p-12 text-center" data-testid="applications-empty">
            <FileText size={40} className="mx-auto text-slate-600 mb-4" />
            <p className="text-slate-400">{t("page.noApplications")}</p>
            <Link to="/jobs"><Button className="mt-5 bg-gradient-to-r from-teal-400 to-cyan-500 text-slate-900 font-semibold">{t("page.findJobs")}</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map((a) => {
              const st = STATUS[a.status] || STATUS.submitted;
              return (
                <div key={a.id} data-testid="application-item" className="liquid-glass rounded-xl p-5">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-slate-700 to-slate-800 border border-slate-700 flex items-center justify-center font-display font-bold text-teal-300">
                        {a.job?.company?.[0] || "W"}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-display font-semibold text-slate-100 truncate">{a.job?.title || "—"}</h3>
                        <p className="text-sm text-slate-400 flex items-center gap-1.5"><Building2 size={13} /> {a.job?.company}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {a.job && <div className="text-teal-300 font-mono text-sm hidden sm:block">{fmtSalary(a.job.salary_min, a.job.salary_max)}</div>}
                      <span data-testid="application-status" className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border mt-1 ${st.color}`}>
                        <st.icon size={12} /> {t(`status.${st.key}`)}
                      </span>
                    </div>
                  </div>
                  <StatusTimeline status={a.status || "submitted"} t={t} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const STEPS = ["submitted", "reviewing", "accepted"];

function StatusTimeline({ status, t }) {
  const rejected = status === "rejected";
  // index of the current stage along the happy path
  const currentIdx = rejected ? 1 : STEPS.indexOf(status === "rejected" ? "reviewing" : status);
  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="status-timeline">
      <div className="flex items-center">
        {STEPS.map((step, i) => {
          const isRejectNode = rejected && i === 2;
          const done = !rejected && i <= currentIdx;
          const active = !rejected && i === currentIdx;
          const label = isRejectNode ? t("status.rejected") : t(`status.${step}`);
          const ringColor = isRejectNode
            ? "bg-red-500/20 border-red-500/50 text-red-300"
            : done
              ? "bg-teal-500/20 border-teal-400 text-teal-300"
              : "bg-slate-800/60 border-slate-700 text-slate-500";
          const lineDone = !rejected && i < currentIdx;
          return (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center gap-1.5 shrink-0" data-testid={`timeline-node-${step}`}>
                <div className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${ringColor} ${active ? "ring-2 ring-teal-400/40" : ""}`}>
                  {isRejectNode ? <XCircle size={14} /> : done ? <Check size={14} /> : <span className="text-[11px] font-mono">{i + 1}</span>}
                </div>
                <span className={`text-[10px] font-medium ${isRejectNode ? "text-red-300" : done ? "text-teal-300" : "text-slate-500"}`}>{label}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 -mt-4 rounded ${rejected ? (i === 0 ? "bg-teal-400/60" : "bg-red-500/40") : lineDone ? "bg-teal-400/60" : "bg-slate-700"}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

